import { useMemo } from 'react'

import { useAtomValue } from 'jotai'

import {
  invoiceBillingPeriodAtom,
  invoiceCurrencyAtom,
  lineItemGroupsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { formatLineItemGroups } from '@sequencehq/invoice-content'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export type LineItemGroup = {
  id: string
  name: string
  description?: string
  servicePeriod?: string
  hasCreditGrantLineItems: boolean
  lineItems: LineItem[]
}

export type LineItem = {
  id: string
  name: string
  description?: string
  qty: string
  rate: string
  tax: string
  amount: string
  servicePeriod?: string
  isCreditGrantLineItem: boolean
}

type UseLineItemsTable = () => LineItemGroup[]

export const useLineItemsTable: UseLineItemsTable = () => {
  const clientLineItemGroups = useAtomValue(lineItemGroupsAtom)
  const currency = useAtomValue(invoiceCurrencyAtom)
  const invoiceBillingPeriod = useAtomValue(invoiceBillingPeriodAtom)
  const { showServicePeriodsAlways } = useFeatureFlags()

  const lineItemGroups: LineItemGroup[] = useMemo(() => {
    const formatted = formatLineItemGroups({
      lineItemGroups: clientLineItemGroups,
      currency,
      invoiceBillingPeriod,
      alwaysShowServicePeriod: showServicePeriodsAlways
    })

    const creditGrantLineItemIds = new Set(
      (clientLineItemGroups ?? [])
        .flatMap(group => group.lineItems)
        .filter(
          item =>
            (item.creditGrantIds ?? []).length > 0 ||
            Boolean(item.creditGrantId)
        )
        .map(item => item.id)
    )

    return formatted.map(group => {
      const lineItems = group.lineItems.map(lineItem => ({
        ...lineItem,
        isCreditGrantLineItem: creditGrantLineItemIds.has(lineItem.id)
      }))

      return {
        ...group,
        lineItems,
        hasCreditGrantLineItems: lineItems.some(
          item => item.isCreditGrantLineItem
        )
      }
    })
  }, [
    clientLineItemGroups,
    currency,
    invoiceBillingPeriod,
    showServicePeriodsAlways
  ])

  return lineItemGroups
}
